import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  IconBan,
  IconBell,
  IconBellPlus,
  IconBook2,
  IconBrain,
  IconCalendarEvent,
  IconCalendarWeek,
  IconCalendarX,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconInfoCircle,
  IconLeaf,
  IconLifebuoy,
  IconPillFilled,
  IconPillOff,
  IconRestore,
  IconStethoscope,
  IconTrash,
} from "@tabler/icons-react";
import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MBadge from "@/components/ui/atoms/MBadge";
import MOptionsMenu from "@/components/ui/atoms/MOptionsMenu";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { documentService } from "@/services/documents.service";
import { remindersService } from "@/services/reminders.service";
import { usePillbox } from "@/hooks/usePillbox";
import {
  MedicineInfo,
  MedicineItemType,
  MedicineSource,
  MedicineStatus,
  MedicineTimingRuleType,
  PillboxHistoryEntry,
  PillboxItem,
} from "@/types/pillbox";
import { Reminders, ReminderType } from "@/types/reminders";
import { FilterOperation, FilterType } from "@/types/filter";
import {
  getEmphasizedText,
  getTertiaryText,
} from "@/pages/reminders/remindersHelper";
import {
  formatDisplayDate,
  formatFrequencyLine,
  formatMedicineTitle,
} from "./utils/formatMedicine";
import { buildReminderPrefillFromPillboxItem } from "./utils/reminderPrefill";
import DetailRow from "./components/DetailRow";
import StopMedicationModal from "./components/StopMedicationModal";
import DeleteMedicationModal from "./components/DeleteMedicationModal";
import MedicineAboutModal from "./components/MedicineAboutModal";
import styles from "./MedicineDetail.module.css";

// Temporarily hidden from UX (not removed) — flip back to true to re-enable.
const SHOW_ABOUT_MEDICINE = false;

const MedicineDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const {
    memberId = "",
    itemType: itemTypeParam,
    id = "",
  } = useParams<{ memberId: string; itemType: string; id: string }>();
  const itemType =
    itemTypeParam === MedicineItemType.SUPPLEMENT
      ? MedicineItemType.SUPPLEMENT
      : MedicineItemType.MEDICINE;
  const { fetchItem, fetchHistory, stopItem, deleteItem, fetchMedicineInfo } =
    usePillbox(memberId);

  const [item, setItem] = useState<PillboxItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<
    PillboxHistoryEntry[] | null
  >(null);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingPrescription, setOpeningPrescription] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutLoading, setAboutLoading] = useState(false);
  const [aboutError, setAboutError] = useState(false);
  const [aboutInfo, setAboutInfo] = useState<MedicineInfo | null>(null);
  const [linkedReminder, setLinkedReminder] = useState<Reminders | null>(null);

  useEffect(() => {
    if (!memberId || !id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchItem(itemType, id)
      .then((data) => {
        if (!cancelled) setItem(data);
      })
      .catch(() => {
        if (!cancelled) setItem(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, id, itemType, fetchItem]);

  // A reminder created via "Add Reminder" (below) is linked back to this
  // medicine via sourceId — look it up so we can show its schedule and
  // route "Add Reminder" to "Edit Reminder" instead once one exists.
  useEffect(() => {
    if (!memberId || !id) return;
    let cancelled = false;
    remindersService
      .getReminders(memberId, {
        filters: {
          type: {
            operation: FilterOperation.EQUAL,
            value: ReminderType.MEDICATION,
            type: FilterType.STRING,
          },
          sourceId: {
            operation: FilterOperation.EQUAL,
            value: id,
            type: FilterType.STRING,
          },
        },
        sort: [],
        pagination: { page: 1, pageSize: 1 },
      })
      .then((response) => {
        if (!cancelled)
          setLinkedReminder((response.data[0] as Reminders) ?? null);
      })
      .catch(() => {
        if (!cancelled) setLinkedReminder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, id]);

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <MLoader />
      </div>
    );
  }

  if (!item) {
    return (
      <div className={styles.root}>
        <div className={styles.simpleHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate("/pillbox")}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
        </div>
        <MEmptyCard
          className={styles.emptyCard}
          icon={<IconPillOff size={64} stroke={1.5} color="#AD95D1" />}
          primaryTitle={t("pillbox.detail.notFound")}
        />
      </div>
    );
  }

  const isSupplement = item.itemType === MedicineItemType.SUPPLEMENT;
  const isSos = item.timingRuleType === MedicineTimingRuleType.CONDITIONAL;
  const isManual = item.source === MedicineSource.MANUAL;
  const isPast = item.status === MedicineStatus.STOPPED;
  // Header avatar icon color: past > SOS > default (past takes priority
  // since a stopped SOS medicine still shows the "past" gray, not orange).
  const avatarIconColor = isPast ? "#E2E2E2" : isSos ? "#D47F3A" : "#45168C";
  // Medicines are only ever opened from a Pillbox landing screen — always
  // return to the one matching this item's category, regardless of actual
  // browser history (e.g. a deep link or a detour through Reminders).
  const handleBack = () => {
    if (isPast) {
      navigate("/pillbox/past-medications");
    } else if (isSos) {
      navigate("/pillbox/sos");
    } else {
      // Forward which tab (List/Schedule) this medicine was opened from
      // (set by PillboxLanding's handleSelectListItem/handleSelectDose) so
      // the landing screen restores it instead of defaulting to Schedule.
      const fromView = (
        location.state as { fromView?: "schedule" | "list" } | null
      )?.fromView;
      navigate("/pillbox", { state: { viewMode: fromView } });
    }
  };
  const showDoctorBadge =
    item.source === MedicineSource.PRESCRIPTION && item.doctorName;
  const doctorBadgeLabel = showDoctorBadge
    ? [item.doctorName, item.dateOfVisit && formatDisplayDate(item.dateOfVisit)]
        .filter(Boolean)
        .join(" ")
    : undefined;

  const handleEditDetails = () => {
    // Forward fromView (List/Schedule) through to the edit screen so its own
    // Back/Save navigation (see AddMedicine.tsx) can restore the same tab —
    // without this, editing from List view always lands back on Schedule,
    // since fromView here would otherwise be lost.
    const fromView = (
      location.state as { fromView?: "schedule" | "list" } | null
    )?.fromView;
    // Also forward this exact detail page's own URL — editing was opened
    // FROM here, so Back/Save on the edit screen should return here too,
    // not jump past this screen straight to the Pillbox landing tabs (which
    // is only correct for edits opened directly from a List/Schedule row,
    // see PillboxLanding's own handleEditDetails).
    navigate("/pillbox/add-medicine", {
      state: { mode: "edit", item, fromView, returnTo: location.pathname },
    });
  };

  const handleRestore = () =>
    navigate("/pillbox/add-medicine", { state: { mode: "restore", item } });

  const reminderPrefill = buildReminderPrefillFromPillboxItem(item);
  // Both opened from this detail page — Back/Save on the reminder screen
  // must return here (see medication/index.tsx's returnTo handling), not
  // the generic reminders list or Pillbox landing.
  const handleAddReminder = () =>
    navigate("/reminders/medication", {
      state: { pillboxPrefill: reminderPrefill, returnTo: location.pathname },
    });
  const handleOpenReminder = () => {
    if (!linkedReminder) return;
    navigate("/reminders/medication", {
      state: { reminder: linkedReminder, returnTo: location.pathname },
    });
  };

  const handleToggleHistory = () => {
    setHistoryOpen((v) => !v);
    if (historyEntries || historyLoading) return;
    setHistoryLoading(true);
    fetchHistory(item.itemType, item.id)
      .then(setHistoryEntries)
      .catch(() => setHistoryEntries([]))
      .finally(() => setHistoryLoading(false));
  };

  const handleOpenAbout = async () => {
    setAboutOpen(true);
    if (aboutInfo || aboutLoading) return;
    setAboutLoading(true);
    setAboutError(false);
    try {
      const info = await fetchMedicineInfo(item.name);
      setAboutInfo(info);
    } catch {
      setAboutError(true);
    } finally {
      setAboutLoading(false);
    }
  };

  const handleOpenPrescription = async () => {
    if (!item.documentId || openingPrescription) return;
    setOpeningPrescription(true);
    try {
      const document = await documentService(memberId).getById(item.documentId);
      navigate("/documents/view", {
        state: {
          document,
          backUrl: `/pillbox/${memberId}/${item.itemType}/${item.id}`,
        },
      });
    } finally {
      setOpeningPrescription(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteItem(item.itemType, item.id);
      navigate("/pillbox/past-medications");
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmStop = async () => {
    setStopping(true);
    try {
      await stopItem(item.itemType, item.id);
      showToast({
        variant: "success",
        message: t("pillbox.stopModal.successMessage", {
          name: formatMedicineTitle(item),
        }),
      });
      navigate(isSos ? "/pillbox/sos" : "/pillbox");
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className={styles.root}>
      <div
        className={`${styles.headerCard} ${isSos ? styles.sosHeaderCard : ""} ${
          isPast ? styles.pastHeaderCard : ""
        }`}
      >
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBack}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
        </div>

        {/* Kebab menu sits in-line with the avatar (not the top-bar with
            Back) per Figma — a left-side spacer matching its own width
            keeps the avatar centered despite the menu occupying the right
            side of this row. */}
        <div className={styles.avatarRow}>
          <span className={styles.avatarRowSpacer} aria-hidden="true" />
          <span className={styles.avatar}>
            {isSupplement ? (
              <IconLeaf size={48} stroke={1.5} color={avatarIconColor} />
            ) : (
              <IconPillFilled size={48} color={avatarIconColor} />
            )}
          </span>
          <span className={styles.menuButton}>
            <MOptionsMenu
              iconSize={16}
              options={
                isPast
                  ? [
                      {
                        id: "restore",
                        label: t("pillbox.optionsMenu.restore"),
                        icon: <IconRestore size={18} />,
                        onClick: handleRestore,
                      },
                      {
                        id: "delete",
                        label: t("pillbox.optionsMenu.delete"),
                        icon: <IconTrash size={18} />,
                        onClick: () => setDeleteModalOpen(true),
                        delete: true,
                      },
                    ]
                  : [
                      {
                        id: "edit",
                        label: t("pillbox.optionsMenu.editDetails"),
                        icon: <IconEdit size={18} />,
                        onClick: handleEditDetails,
                      },
                      ...(linkedReminder
                        ? [
                            {
                              id: "edit-reminder",
                              label: t("pillbox.optionsMenu.editReminder"),
                              icon: <IconBellPlus size={18} />,
                              onClick: handleOpenReminder,
                            },
                          ]
                        : reminderPrefill
                        ? [
                            {
                              id: "add-reminder",
                              label: t("pillbox.optionsMenu.addReminder"),
                              icon: <IconBellPlus size={18} />,
                              onClick: handleAddReminder,
                            },
                          ]
                        : []),
                      {
                        id: "stop",
                        label: t("pillbox.optionsMenu.stopMedication"),
                        icon: <IconBan size={18} />,
                        onClick: () => setStopModalOpen(true),
                        delete: true,
                      },
                    ]
              }
            />
          </span>
        </div>

        {isPast ? (
          <div className={styles.badgeRow}>
            <span className={styles.pastTypeBadge}>
              {t("pillbox.detail.pastBadge")}
            </span>
            <span className={styles.pastTypeBadge}>
              {isSupplement
                ? t("pillbox.addMedicine.supplement")
                : t("pillbox.addMedicine.medicine")}
            </span>
          </div>
        ) : isSos ? (
          <div className={styles.badgeRow}>
            <span className={styles.sosTypeBadge}>
              <IconLifebuoy size={12} />
              {t("pillbox.detail.sosBadge")}
            </span>
            <span className={styles.sosTypeBadge}>
              {isSupplement
                ? t("pillbox.addMedicine.supplement")
                : t("pillbox.addMedicine.medicine")}
            </span>
          </div>
        ) : (
          <MBadge variant="purple" className={styles.typeBadge}>
            {isSupplement
              ? t("pillbox.addMedicine.supplement")
              : t("pillbox.addMedicine.medicine")}
          </MBadge>
        )}

        <h1 className={styles.name}>{formatMedicineTitle(item)}</h1>
        <p className={styles.frequencyLine}>
          {isSos
            ? t("pillbox.detail.asNeeded")
            : formatFrequencyLine(item, item.recurrenceIntervalDays)}
        </p>

        {isManual && (
          <span className={styles.manualBadge}>
            {t("pillbox.detail.manuallyAdded")}
          </span>
        )}

        {doctorBadgeLabel && (
          <MBadge
            variant="purple"
            className={styles.doctorBadge}
            onClick={item.documentId ? handleOpenPrescription : undefined}
          >
            {doctorBadgeLabel}
            <IconChevronRight size={12} />
          </MBadge>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.dateRow}>
          <DetailRow
            icon={<IconCalendarEvent size={12} />}
            label={t("pillbox.detail.startDate")}
            value={
              item.startDate
                ? formatDisplayDate(item.startDate)
                : t("pillbox.detail.notSet")
            }
            isPlaceholder={!item.startDate}
          />
          <DetailRow
            icon={<IconCalendarX size={12} />}
            label={t("pillbox.detail.stopDate")}
            value={
              // For a stopped course, the date that actually matters is when
              // it was stopped (stoppedAt) — not the originally scheduled
              // endDate, which is frequently unset entirely (e.g. Health
              // Profile-only medicines that predate Pillbox never had one)
              // even though the medicine was genuinely, manually stopped.
              // For an active course, endDate is the right thing to show
              // (its planned/scheduled stop date).
              (isPast ? item.stoppedAt : item.endDate)
                ? formatDisplayDate((isPast ? item.stoppedAt : item.endDate)!)
                : t("pillbox.detail.notSet")
            }
            isPlaceholder={!(isPast ? item.stoppedAt : item.endDate)}
          />
        </div>

        <DetailRow
          icon={<IconStethoscope size={12} />}
          label={t("pillbox.detail.doctorName")}
          value={item.doctorName || t("pillbox.detail.notAdded")}
          isPlaceholder={!item.doctorName}
        />

        {/* Opens the linked reminder if one's been set (via "Add Reminder"
            in the options menu above); otherwise starts that same flow,
            when this medicine's schedule even supports a reminder. */}
        <DetailRow
          icon={<IconBell size={12} />}
          label={t("pillbox.detail.reminder")}
          value={
            linkedReminder ? (
              <>
                {getEmphasizedText(linkedReminder, t)}
                <br />
                {getTertiaryText(linkedReminder)}
              </>
            ) : (
              t("pillbox.detail.notSet")
            )
          }
          trailingIcon={
            linkedReminder || reminderPrefill ? (
              <IconChevronRight size={16} />
            ) : undefined
          }
          multiline
          isPlaceholder={!linkedReminder}
          onClick={
            linkedReminder
              ? handleOpenReminder
              : reminderPrefill
              ? handleAddReminder
              : undefined
          }
        />

        <DetailRow
          icon={<IconBrain size={12} />}
          label={t("pillbox.detail.reasonForTaking")}
          value={item.reasonForTaking || t("pillbox.detail.notAdded")}
          isPlaceholder={!item.reasonForTaking}
        />

        <DetailRow
          icon={<IconBook2 size={12} />}
          label={t("pillbox.detail.instructions")}
          value={item.notes || t("pillbox.detail.notAdded")}
          multiline
          isPlaceholder={!item.notes}
        />

        <button
          type="button"
          className={styles.historyRow}
          onClick={handleToggleHistory}
        >
          <span className={styles.historyLabel}>
            <span className={styles.historyIcon}>
              <IconCalendarWeek size={12} />
            </span>
            {t("pillbox.detail.history")}
          </span>
          <IconChevronDown
            size={16}
            className={historyOpen ? styles.chevronOpen : ""}
          />
        </button>
        {historyOpen && (
          <>
            {historyLoading ? (
              <div className={styles.historyLoaderWrap}>
                <MLoader size={20} />
              </div>
            ) : !historyEntries || historyEntries.length === 0 ? (
              <p className={styles.historyEmpty}>
                {t("pillbox.detail.historyEmpty")}
              </p>
            ) : (
              <div className={styles.historyTable}>
                <div className={styles.historyColumnHead}>
                  <span>{t("pillbox.detail.startDate")}</span>
                  <span>{t("pillbox.detail.stopDate")}</span>
                </div>
                {historyEntries.map((entry, index) => (
                  <div
                    className={styles.historyRowValues}
                    key={`${entry.startDate}-${index}`}
                  >
                    <span>{formatDisplayDate(entry.startDate)}</span>
                    <span>
                      {entry.stopDate
                        ? formatDisplayDate(entry.stopDate)
                        : t("pillbox.detail.ongoing")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {SHOW_ABOUT_MEDICINE && (
          <button
            type="button"
            className={styles.aboutCard}
            onClick={handleOpenAbout}
          >
            <span className={styles.aboutIcon}>
              <IconInfoCircle size={24} stroke={1.5} color="#4C189A" />
            </span>
            <span className={styles.aboutText}>
              {t("pillbox.detail.about", { name: item.name })}
              <IconChevronRight size={12} />
            </span>
          </button>
        )}
      </div>

      <MedicineAboutModal
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        loading={aboutLoading}
        error={aboutError}
        info={aboutInfo}
      />

      <StopMedicationModal
        open={stopModalOpen}
        onClose={() => setStopModalOpen(false)}
        onConfirm={handleConfirmStop}
        loading={stopping}
      />
      <DeleteMedicationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default MedicineDetail;
